import styled from 'styled-components';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import { useSendFocusEvent } from 'modules/focusManager';
import Tooltip from 'common/ui/Tooltip';
import { usePopupOptionsGetter } from 'hooks/useWindowsRects';
import { getPageType, PageTypes } from 'utils/pageTypes';
import { displayMessage } from './displayMessage';
import { Colors } from 'styles';
import type { GenericSnackBarProps } from 'common/GenericSnackBar/GenericSnackBar';
import { openWindow } from './openWindow';
import { openTab } from './openTab';
import { MENU_WIDTH } from './constants';
import { useEffect, useRef, useState } from 'react';
import type { SendFocusEvent } from 'modules/focusManager';
import type { PopupOptionsGetter } from 'hooks/useWindowsRects';
import {
  draftReportWorklistSmid,
  discardReportState,
  discardReportStateDefault,
} from 'domains/reporter/Reporter/state';
import { useSetRecoilState } from 'recoil';

/** Time to wait until the popup loads before enabling a subsequent window.open call. Prevents
 * multiple windows from opening during the period a popup's about:blank transitions to a route. */
export const WAIT_UNTIL_POPUP_LOADS_MS = 1000;

export const logEvents = ({
  pageType,
  sendEvent,
  viewerIndex,
}: {
  pageType: PageTypes;
  sendEvent: SendFocusEvent;
  viewerIndex: string;
}): void => {
  // Focus the other viewer if it is open. This way, both are easily accessible
  // to the viewer
  if (pageType === PageTypes.VIEWER) {
    sendEvent({ pageType, viewerId: viewerIndex });
  }

  // Timeout is necessary so that both events will fire
  setTimeout(() => {
    sendEvent({ pageType, viewerId: viewerIndex });
  }, 50);
};

type OnClickArguments = {
  to: string;
  navigate?: NavigateFunction;
  openedViewerIds: Set<string>;
  isReporterOpen: boolean;
  isAISandboxOpen: boolean;
  isWorklistOpen: boolean;
  isSearchOpen: boolean;
  isJacketOpen: boolean;
  setSnackbarMessage: (arg1: {
    message: React.ReactNode;
    severity: GenericSnackBarProps['severity'];
  }) => void;
  sendEvent: SendFocusEvent;
  viewerIndex: string;
  getPopupOptions: PopupOptionsGetter;
};

export const onNavMenuItemClick = ({
  to,
  navigate,
  openedViewerIds,
  isReporterOpen,
  isAISandboxOpen,
  isWorklistOpen,
  isSearchOpen,
  isJacketOpen,
  setSnackbarMessage,
  sendEvent,
  viewerIndex,
  getPopupOptions,
}: OnClickArguments) => {
  const pageType = getPageType(to);

  displayMessage({
    openedViewerIds,
    isReporterOpen,
    setSnackbarMessage,
    viewerIndex,
    pageType,
  });
  openWindow({
    to,
    navigate,
    openedViewerIds,
    isReporterOpen,
    isAISandboxOpen,
    isWorklistOpen,
    isSearchOpen,
    isJacketOpen,
    viewerIndex,
    getPopupOptions,
    pageType,
  });

  logEvents({ pageType, sendEvent, viewerIndex });
};

type LinkStyleProps = {
  $selected: boolean;
  $isRelatedWindowOpen: boolean;
  $disabled?: boolean;
};

const StyledLink = styled(Link)<LinkStyleProps>`
  app-region: no-drag;
  color: ${(props) =>
    props.$isRelatedWindowOpen && !props.$selected ? Colors.mainBlue : Colors.white};
  width: ${MENU_WIDTH};
  height: 5.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.$selected ? Colors.mainBlue36Percent : 'transparent')};
  transition: 0.2s ease-in-out;
  flex-shrink: 0;
  position: relative;
  pointer-events: auto;

  &[data-disabled='true'] {
    cursor: not-allowed;
    background-color: ${(props) => (props.$selected ? Colors.mainBlue36Percent : 'transparent')};
    color: ${(props) =>
      props?.$disabled
        ? Colors.whiteDisable
        : props.$isRelatedWindowOpen && !props.$selected
          ? Colors.mainBlue
          : Colors.white};
    &::after {
      height: 0%;
      opacity: 0;
    }
  }

  &:hover {
    background-color: ${Colors.mainBlue6Percent};
    color: ${(props) =>
      props.$isRelatedWindowOpen && !props.$selected ? Colors.blue6 : Colors.white};
  }

  &[data-disabled='true']:hover {
    background-color: ${(props) => (props.$selected ? Colors.mainBlue36Percent : 'transparent')};
    color: ${Colors.whiteDisable};
  }

  &::after {
    content: '';
    height: ${(props) => (props.$isRelatedWindowOpen && !props.$selected ? '20%' : '0%')};
    opacity: ${(props) => (props.$isRelatedWindowOpen && !props.$selected ? 1 : 0)};
    transition:
      height 0.5s ease-in-out,
      opacity 0.5s ease-in-out;
    position: absolute;
    right: 2px;
    border-radius: 2.5px;
    width: 3px;
    background-color: currentColor;
  }
`;

export const MenuItem = ({
  selected,
  isRelatedWindowOpen,
  to,
  viewerIndex,
  children,
  label,
  setSnackbarMessage,
  openedViewerIds,
  isReporterOpen,
  isAISandboxOpen,
  isWorklistOpen,
  isSearchOpen,
  isJacketOpen,
  isExternal = false,
  disabled = false,
  onClick,
  tooltip,
  'data-testid': dataTestid,
  'aria-label': ariaLabel,
}: {
  selected: boolean;
  isRelatedWindowOpen: boolean;
  to: string;
  children: React.ReactNode;
  label: string;
  setSnackbarMessage: (arg1: {
    message: React.ReactNode;
    severity: GenericSnackBarProps['severity'];
  }) => void;
  viewerIndex: string;
  openedViewerIds: Set<string>;
  isReporterOpen: boolean;
  isAISandboxOpen: boolean;
  isWorklistOpen: boolean;
  isSearchOpen: boolean;
  isJacketOpen: boolean;
  isExternal?: boolean;
  disabled?: boolean;
  onClick?: (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  tooltip?: string;
  ['data-value']?: string;
  ['data-testid']?: string;
  ['aria-label']?: string;
}): React.ReactElement => {
  const navigate = useNavigate();
  const sendEvent = useSendFocusEvent();
  const getPopupOptions = usePopupOptionsGetter();
  const [isLoadingReporter, setIsLoadingReporter] = useState<boolean>(false);
  const loadingTimeout = useRef<Nullable<number>>(null);
  const setDraftReportWorklistSmid = useSetRecoilState(draftReportWorklistSmid);
  const setDiscardReportOptions = useSetRecoilState(discardReportState);

  // Sets a 1s timer after the initial click to prevent the reporter from opening multiple windows.
  // The `isLoadingReporter` value controls whether `window.open` will be triggered.
  const queueReporterLoadingReset = () => {
    setIsLoadingReporter(true);
    loadingTimeout.current = window.setTimeout(() => {
      setIsLoadingReporter(false);
    }, WAIT_UNTIL_POPUP_LOADS_MS);
  };
  const clearLoadingTimeout = () => {
    if (loadingTimeout.current != null) {
      clearTimeout(loadingTimeout.current);
      loadingTimeout.current = null;
    }
  };

  useEffect(
    () => () => {
      if (loadingTimeout.current != null) clearTimeout(loadingTimeout.current);
    },
    []
  );

  const props = {
    'data-testid': dataTestid,
    'data-analytics-name': `navbar-link-${dataTestid ?? label}`,
    'data-page-open': isRelatedWindowOpen,
    'aria-label': ariaLabel,
    $selected: selected,
    $isRelatedWindowOpen: isRelatedWindowOpen,
    $disabled: disabled,
    'aria-disabled': disabled || undefined,
    'data-disabled': disabled ? 'true' : undefined,
    tabIndex: disabled ? -1 : 0,
  } as const;

  return (
    <Tooltip content={tooltip ?? label} placement="right">
      {isExternal ? (
        <StyledLink
          to="#"
          onClick={(evt) => {
            evt.preventDefault();
            if (disabled) return;

            if (onClick) {
              onClick(evt);
              return;
            }

            openTab(to, getPopupOptions(to) ?? { width: 1024, height: 900, top: 10, left: 10 });
          }}
          {...props}
        >
          {children}
        </StyledLink>
      ) : (
        <StyledLink
          to={to}
          onClick={(evt) => {
            evt.preventDefault();
            if (disabled) return;

            if (onClick) {
              onClick(evt);
              return;
            }

            clearLoadingTimeout();
            queueReporterLoadingReset();

            if (
              getPageType(to) === PageTypes.REPORTER &&
              !window.location.pathname.includes(PageTypes.REPORTER)
            ) {
              setDraftReportWorklistSmid(null);
              setDiscardReportOptions(discardReportStateDefault);
            }

            onNavMenuItemClick({
              to,
              navigate,
              openedViewerIds,
              isReporterOpen: isLoadingReporter || isReporterOpen,
              isAISandboxOpen,
              isWorklistOpen,
              isSearchOpen,
              isJacketOpen,
              setSnackbarMessage,
              sendEvent,
              viewerIndex,
              getPopupOptions,
            });
          }}
          {...props}
        >
          {children}
        </StyledLink>
      )}
    </Tooltip>
  );
};
